/**
 * library-audio — pure helpers that turn theory-engine objects into AudioPlans
 * for the /library reference page.
 *
 * Each plan plays a brief musical phrase — scale ascending, chord arpeggio +
 * block, interval as melodic + harmonic, progression with smooth voice
 * leading. The page wires these into the shared web AudioPlayer.
 *
 * Pure: no DOM, no audio APIs, no random — same inputs always produce the
 * same plan. That makes them easy to unit-test and trivial to swap engines.
 */

import { AudioPlanBuilder, type AudioPlan } from "@music-app/exercise-generator";
import {
  type Pitch,
  type ScaleType,
  type ChordQuality,
  type Key,
  type ProgressionTemplate,
  scale,
  scalePitches,
  chord,
  chordPitches,
  diatonicTriads,
  realizeProgression,
  toMidi,
  pitch,
} from "@music-app/theory-engine";

/* ─── Defaults ────────────────────────────────────────────────────────────── */

const NOTE_DURATION = 0.45;
const NOTE_GAIN = 0.16;
const CHORD_NOTE_GAIN = 0.08;
const BASS_GAIN = 0.11;
const ROOT_OCTAVE = 4;

/** Build a Pitch at our default reference octave for a chromatic root. */
export function pitchAtRootOctave(letter: Pitch["letter"], accidental: Pitch["accidental"]): Pitch {
  return pitch(letter, accidental, ROOT_OCTAVE);
}

/* ─── Scale ───────────────────────────────────────────────────────────────── */

/** Play a scale ascending, then back down to the root for grounding. */
export function scalePlan(root: Pitch, type: ScaleType): AudioPlan {
  const sc = scale(root, type);
  const ascendingMidis = scalePitches(sc).map(toMidi);
  // Cap the top with the octave above the root so the listener hears
  // the scale "land" — most scales in SCALE_PATTERNS don't include the octave.
  const topMidi = ascendingMidis[0]! + 12;
  const sequence = [...ascendingMidis, topMidi];

  const b = new AudioPlanBuilder();
  for (const m of sequence) {
    b.addNote(m, NOTE_DURATION, NOTE_GAIN, "melody").advance(NOTE_DURATION);
  }
  return b.build();
}

/* ─── Chord ───────────────────────────────────────────────────────────────── */

/**
 * Play a chord: brief arpeggio (low → high), then block chord with bass note.
 * The arpeggio teaches the chord's anatomy; the block chord delivers the gestalt.
 */
export function chordPlan(root: Pitch, quality: ChordQuality): AudioPlan {
  const c = chord(root, quality);
  const midis = chordPitches(c).map(toMidi);

  const b = new AudioPlanBuilder();
  // Arpeggio
  for (const m of midis) {
    b.addNote(m, NOTE_DURATION, NOTE_GAIN, "melody").advance(NOTE_DURATION);
  }
  b.advance(0.15);
  // Block chord with bass
  const rootMidi = midis[0]!;
  b.addNote(rootMidi - 12, 1.4, BASS_GAIN, "bass");
  b.addChord(midis, 1.4, CHORD_NOTE_GAIN, "harmony");
  b.advance(1.6);
  return b.build();
}

/* ─── Interval ────────────────────────────────────────────────────────────── */

/** Catalog of intervals shown in the library, in standard ascending order. */
export interface LibraryInterval {
  /** Display label like "P5", "M3". */
  label: string;
  /** Long name shown alongside, like "Perfect fifth". */
  longName: string;
  /** Semitones up from the root. */
  semitones: number;
}

export const LIBRARY_INTERVALS: LibraryInterval[] = [
  { label: "P1", longName: "Perfect unison",   semitones: 0 },
  { label: "m2", longName: "Minor second",     semitones: 1 },
  { label: "M2", longName: "Major second",     semitones: 2 },
  { label: "m3", longName: "Minor third",      semitones: 3 },
  { label: "M3", longName: "Major third",      semitones: 4 },
  { label: "P4", longName: "Perfect fourth",   semitones: 5 },
  { label: "A4", longName: "Tritone (A4/d5)",  semitones: 6 },
  { label: "P5", longName: "Perfect fifth",    semitones: 7 },
  { label: "m6", longName: "Minor sixth",      semitones: 8 },
  { label: "M6", longName: "Major sixth",      semitones: 9 },
  { label: "m7", longName: "Minor seventh",    semitones: 10 },
  { label: "M7", longName: "Major seventh",    semitones: 11 },
  { label: "P8", longName: "Perfect octave",   semitones: 12 },
];

/**
 * Play an interval: lower note, upper note (melodic), then both together
 * (harmonic). This is the standard ear-training presentation.
 */
export function intervalPlan(rootMidi: number, semitones: number): AudioPlan {
  const upper = rootMidi + semitones;
  const b = new AudioPlanBuilder();
  b.addNote(rootMidi, NOTE_DURATION, NOTE_GAIN, "melody").advance(NOTE_DURATION);
  b.addNote(upper, NOTE_DURATION, NOTE_GAIN, "melody").advance(NOTE_DURATION + 0.2);
  // Harmonic
  b.addNote(rootMidi, 1.2, CHORD_NOTE_GAIN, "harmony");
  b.addNote(upper, 1.2, CHORD_NOTE_GAIN, "harmony");
  b.advance(1.4);
  return b.build();
}

/* ─── Progression ─────────────────────────────────────────────────────────── */

/**
 * Materialize a progression and play it with light voice leading and a
 * grounding bass line. Mirrors the cadence builder in exercise-generator
 * but tailored for arbitrary diatonic templates.
 */
export function progressionPlan(template: ProgressionTemplate, k: Key): AudioPlan {
  const realized = realizeProgression(template, k);
  // Build triad MIDI clusters in close root position around middle C.
  const triadVoicings = realized.map((c) => chordPitches(c).slice(0, 3).map(toMidi));
  // Reference triad: the I (or i) chord of the key, used to pin octave.
  const tonicTriad = diatonicTriads(k)[0]!;
  const tonicMidis = chordPitches(tonicTriad).slice(0, 3).map(toMidi);

  const b = new AudioPlanBuilder();
  let prev: number[] | null = null;
  const dur = 0.95;
  const gap = 0.05;
  for (const triad of triadVoicings) {
    const voicing = smoothVoicing(prev, triad, tonicMidis);
    prev = voicing;
    const bassMidi = voicing[0]! - 12;
    b.addNote(bassMidi, dur, BASS_GAIN, "bass");
    b.addChord(voicing, dur, CHORD_NOTE_GAIN, "harmony");
    b.advance(dur + gap);
  }
  // Final breath
  b.advance(0.3);
  return b.build();
}

/**
 * Smooth voice leading: pick the octave shift for each chord tone that
 * minimizes total semitone movement from the previous voicing. On the first
 * chord, anchor to a comfortable register based on the tonic triad.
 */
function smoothVoicing(
  prev: number[] | null,
  target: number[],
  tonicAnchor: number[],
): number[] {
  if (!prev) {
    // Anchor the first chord near the tonic's register so the whole
    // progression sits in a singable range regardless of which degree
    // starts it. Then spread the close-position triad.
    const root = target[0]!;
    const anchorRoot = tonicAnchor[0]!;
    let octaveShift = 0;
    while (root + octaveShift - anchorRoot > 6) octaveShift -= 12;
    while (anchorRoot - (root + octaveShift) > 6) octaveShift += 12;
    return spread(target.map((m) => m + octaveShift));
  }

  let best = target;
  let bestCost = Infinity;
  const offsets = [-12, 0, 12];
  for (const o0 of offsets) {
    for (const o1 of offsets) {
      for (const o2 of offsets) {
        const v = [target[0]! + o0, target[1]! + o1, target[2]! + o2].sort((a, b) => a - b);
        if (v[0]! < 48 || v[v.length - 1]! > 76) continue;
        let cost = 0;
        for (let i = 0; i < v.length; i++) cost += Math.abs(v[i]! - prev[i]!);
        if (cost < bestCost) {
          bestCost = cost;
          best = v;
        }
      }
    }
  }
  return best;
}

/** Spread a close-position triad so the top note sits at least a third above the third. */
function spread(triad: number[]): number[] {
  const sorted = [...triad].sort((a, b) => a - b);
  const root = sorted[0]!;
  const third = sorted[1]!;
  const fifth = sorted[2]!;
  const top = fifth < root + 7 ? fifth + 12 : fifth;
  return [root, third, top];
}
